// ABOUTME: The rail's third accordion item — Privacy & Data Preferences (Figma 113:6517).
// ABOUTME: A RailPanel/MobilePanel sibling: same expand/collapse contract, unit-scaled either way.

import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { CONSENT_TEXT } from './railGeometry';
import { MIN_TEXT_PX } from './mobileGeometry';
import { useViewportSize } from '../Projectionist/useViewportSize';
import { ConsentCategoryList } from '../../consent/ConsentCategoryList';
import { DEFAULT_CATEGORIES, DISCLOSURE } from '../../consent/consentConfig';

const seedFrom = (consent) => ({ ...DEFAULT_CATEGORIES, ...(consent?.categories || {}) });

// Daylight between the panel's own bottom edge and the CC bar it must clear.
export const FIT_GUTTER_PX = 24;
// Never shrink the unit past this share of its base (2026-09-03). This was an absolute 0.3px,
// described as matching the railUnitFor/mobileUnitFor floors — those are 0.43 and 0.24. At 0.3 the
// desktop body copy reached 8px on a 1280x720 laptop, and on a phone narrower than 365px the "floor"
// sat ABOVE the base unit, so an overflowing panel grew by 14% instead of shrinking. The type now
// carries its own real-px floor (`type()` below) and whatever those floors leave over scrolls inside
// the rows list, so the unit no longer has to buy the whole fit on its own.
const FIT_MIN_RATIO = 0.7;
// How many consecutive animation frames this panel's own screen position has to hold still before
// the fit measurement trusts it — see the settle-poll below.
const FIT_STABLE_FRAMES = 3;
// Safety cap so a measurement that never stabilises (shouldn't happen — every rail transition is a
// fixed 0.28s) still resolves instead of polling forever.
const FIT_MAX_FRAMES = 90;

// The floor the panel fits above: the CC bar, as it always was. SPEC-82 Rev 3 injects its own (the panel moved under the hamburger,
// where the PDP pill and the tag row can sit below it too); every existing caller keeps exactly this measurement.
export const captionBarTop = () => {
  const bar = document.querySelector('[data-testid="reel-caption-bar"]');
  return bar ? bar.getBoundingClientRect().top : window.innerHeight;
};

/**
 * @param {{
 *   unit: (authoredPx: number) => string,
 *   geometry: object,      // CONSENT_PANEL (desktop) or MOBILE_CONSENT (mobile)
 *   text?: object,         // CONSENT_TEXT (desktop, default) or MOBILE_CONSENT_TEXT (mobile)
 *   panelW: number,        // authored width of the outer frosted container (PANEL_W / MOBILE_PANEL.w)
 *   widthCss?: string,     // overrides `unit(panelW)` — IconRail passes its viewport-capped panelWidth()
 *   panelPad: number,      // authored padding (PANEL_PAD / MOBILE_PANEL.pad)
 *   panelRadius: number,   // authored radius (PANEL_RADIUS / MOBILE_PANEL.radius)
 *   baseUnitPx: number,    // caller's railUnitFor/mobileUnitFor(viewportWidth) — the normal, un-shrunk
 *                          // `--u` this panel starts its own fit measurement from
 *   glyph: { src: string, w: number, h: number },
 *   consent: object,
 *   onSave: (categories: object) => void,
 *   onCollapse: () => void,
 *   onOpenPrivacyPolicy: () => void,
 *   floorTop?: (content: Element) => number, // SPEC-82 Rev 3: the y the panel must stay above (default: the CC bar's top)
 *   refitKey?: string,     // SPEC-82 Rev 3: a change re-measures the fit (something below the panel appeared or moved)
 * }} props
 */
function ConsentPanel({
  unit, geometry, text = CONSENT_TEXT, panelW, widthCss = null, panelPad, panelRadius, baseUnitPx, glyph,
  consent, onSave, onCollapse, onOpenPrivacyPolicy, floorTop = captionBarTop, refitKey = null,
}) {
  const u = unit;
  // Real-px floor on the type, applied proportionally (the rule PrivacyPolicy.js already uses):
  // MIN_TEXT_PX for the smallest token (row body) and every larger token gets that floor scaled by
  // its own ratio to it, so the hierarchy survives the floor. At the desktop unit's own floor (0.43)
  // nothing binds — 26.667 sets at 11.47 — so a laptop that fits unshrunk renders exactly as before;
  // it lifts the shrunk panel on a short laptop, and the smallest handsets, where every other mobile
  // text already had this floor and this panel alone did not (2026-09-03).
  const type = (authored) => `max(${((MIN_TEXT_PX * authored) / text.rowBody).toFixed(3)}px, ${u(authored)})`;
  const [selected, setSelected] = useState(() => seedFrom(consent));

  // Re-seed every time the panel opens, same contract PreferencesCenter had.
  useEffect(() => { setSelected(seedFrom(consent)); }, [consent]);

  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  // This panel's three categories are fixed copy, never data-driven like the cart/wishlist grids —
  // so unlike those (which fall back to their own item-grid scroll for genuinely unbounded content)
  // it can promise to always clear the CC bar rather than scroll. An authored-px height estimate
  // can't keep that promise: it undershot the real wrapped line count of the longer category copy
  // (reported 2026-08-28 — the panel still scrolled with its footer buttons cut off), and it assumed
  // the rail's own 3840x2160 aspect ratio, which a real browser window rarely matches exactly either.
  // Measuring the actual rendered content against the CC bar's actual position is the only version of
  // this that holds regardless of viewport shape, DPR, or a future copy edit — so this shrinks `--u`
  // for the panel's own subtree (a CSS custom property cascades to descendants only, so the two
  // sibling collapsed chips are untouched) only as far as a real overflow demands.
  const contentRef = useRef(null);
  // { unitPx, maxHeightPx } once measured; null while a fresh measurement at the natural size is due.
  const [fit, setFit] = useState(null);
  // The viewport HEIGHT is a dependency in its own right: `baseUnitPx` is width-derived and, on every
  // desktop width under 2064px, a constant — so a window dragged shorter, an iPad split view or a
  // collapsing toolbar used to leave a stale fit in place (2026-09-03).
  const { height: viewportHeight } = useViewportSize();
  useLayoutEffect(() => {
    // `baseUnitPx` changing (a viewport resize) means any previous fit no longer applies — measuring
    // AT that previous, possibly-shrunk size would compound the shrink on every resize instead of
    // re-deriving it fresh. Render one pass at the natural size first; the effect below measures it.
    setFit(null);
  }, [baseUnitPx, viewportHeight, refitKey]);
  useLayoutEffect(() => {
    if (fit !== null) return; // already measured this pass
    const content = contentRef.current;
    if (!content || !baseUnitPx) return;
    // Opening this panel while cart or wishlist was already expanded auto-collapses that sibling —
    // but its collapse is a 0.28s height animation, not instant, and this panel mounts at the same
    // moment. Measuring immediately caught that sibling still mid-collapse, still occupying real
    // height above this panel, which pushed the measured `top` far down the screen and made
    // `available` look tiny — shrinking this panel far more than it ever needed (reported
    // 2026-08-28: correct size opening from closed, "extremely small" opening over an open sibling).
    // Polling on animation frames until this panel's own screen position stops moving — rather than
    // hardcoding the sibling's 280ms — means this holds even if that duration ever changes.
    let cancelled = false;
    let prevTop = null;
    let stableFrames = 0;
    let frame = 0;
    const settle = () => {
      if (cancelled) return;
      const top = content.getBoundingClientRect().top;
      stableFrames = (prevTop !== null && Math.abs(top - prevTop) < 0.5) ? stableFrames + 1 : 0;
      prevTop = top;
      frame += 1;
      if (stableFrames < FIT_STABLE_FRAMES && frame < FIT_MAX_FRAMES) {
        requestAnimationFrame(settle);
        return;
      }
      const barTop = floorTop(content);
      const available = barTop - top - FIT_GUTTER_PX;
      const natural = content.scrollHeight;
      const overflows = available > 0 && natural > available;
      // Shrink the unit toward the fit, but never below FIT_MIN_RATIO of the base and never ABOVE
      // the base (the old absolute floor could exceed a small phone's unit and enlarge the panel).
      // The measured `available` also becomes a hard ceiling on the content box: once the type
      // floors stop the shrink short of a full fit, the rows list scrolls instead of the panel's
      // footer walking into the caption bar.
      setFit(overflows
        ? {
          unitPx: Math.min(baseUnitPx, Math.max(baseUnitPx * FIT_MIN_RATIO, baseUnitPx * (available / natural))),
          maxHeightPx: available,
        }
        : { unitPx: baseUnitPx, maxHeightPx: null });
    };
    const raf = requestAnimationFrame(settle);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [fit, baseUnitPx, floorTop]);
  const resolvedUnitPx = fit?.unitPx ?? baseUnitPx;
  const maxHeightPx = fit?.maxHeightPx ?? null;

  return (
    <motion.div
      key="PRIVACY"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      className="overflow-hidden shrink-0"
      // Overriding `--u` here (rather than leaving it inherited from the rail) only ever shrinks this
      // panel's own subtree — a CSS custom property cascades to descendants, not siblings, so the two
      // collapsed chips beside it are untouched.
      style={{ width: widthCss || u(panelW), ...(resolvedUnitPx ? { '--u': `${resolvedUnitPx}px` } : {}) }}
    >
      <div
        ref={contentRef}
        // The only ceiling here is the MEASURED one from the `useLayoutEffect` above (an earlier
        // authored-aspect-ratio estimate undershot the real content and clipped the panel short of
        // the bar). It is applied to this box, and the rows list below is the flex child that gives
        // way (`min-h-0` + its own scroll), so the CANCEL/SAVE footer stays on screen whatever the
        // type floors leave over. On a viewport where the natural panel already clears the bar
        // `maxHeightPx` is null and nothing here constrains anything.
        className="bg-reel-chrome-panel backdrop-blur-reel-chrome flex flex-col relative"
        style={{
          padding: u(panelPad),
          borderRadius: u(panelRadius),
          ...(maxHeightPx ? { maxHeight: `${maxHeightPx}px` } : {}),
        }}
      >
        <button type="button" aria-label={DISCLOSURE.launcherLabel} aria-expanded="true" onClick={onCollapse} className="reel-touch-target relative block shrink-0 hover:opacity-70 transition-opacity">
          <img src={glyph.src} alt="" aria-hidden="true" style={{ width: u(glyph.w), height: u(glyph.h) }} />
        </button>

        {/* Explicit close affordance, same contract as the cart/wishlist panels (IconRail.js) and
            the client's Figma frame ("Shopping Bag (4)" with an X on the SAME row as the title,
            not floating over the glyph). The CANCEL button below and the glyph both already
            collapse this panel; this is a legible third path sharing their handler, so App.js's
            overlay-close notice to the agent is unaffected by which of the three the guest used. */}
        <div
          className="flex items-center justify-between shrink-0"
          style={{ marginTop: u(geometry.glyphToTitleGap) }}
        >
          <h2 className="m-0 font-reel-heading font-extrabold text-reel-chrome-textPrimary" style={{ fontSize: type(text.title) }}>
            {DISCLOSURE.manageTitle}
          </h2>
          <button
            type="button"
            aria-label="Close privacy panel"
            onClick={onCollapse}
            className="reel-touch-target relative shrink-0 hover:opacity-70 transition-opacity text-reel-chrome-textPrimary"
            style={{ width: u(glyph.h), height: u(glyph.h) }}
          >
            <X aria-hidden="true" style={{ width: u(glyph.h), height: u(glyph.h) }} />
          </button>
        </div>

        <button
          type="button"
          onClick={onOpenPrivacyPolicy}
          className="reel-touch-target relative shrink-0 text-left uppercase font-reel-body underline decoration-from-font text-reel-chrome-textPrimary hover:opacity-80 transition-opacity w-fit"
          style={{ fontSize: type(text.link), marginTop: u(geometry.titleToLinkGap) }}
        >
          {DISCLOSURE.privacyLinkLabel}
        </button>

        <div className="min-h-0 overflow-y-auto" style={{ marginTop: u(geometry.linkToRowsGap) }}>
          <ConsentCategoryList selected={selected} onToggle={toggle} theme="reel" unit={u} type={type} geometry={geometry} text={text} />
        </div>

        <div className="flex shrink-0" style={{ marginTop: u(geometry.footerGap), gap: u(geometry.footerBtnGap) }}>
          <button
            type="button"
            onClick={onCollapse}
            className="reel-touch-target relative flex-1 uppercase bg-reel-cta-primaryBg text-reel-chrome-textInk font-reel-body flex items-center justify-center text-center leading-tight hover:opacity-90 transition-opacity"
            style={{
              minHeight: u(geometry.footerH), borderRadius: u(geometry.rowRadius), fontSize: type(text.cta),
              paddingTop: u(16), paddingBottom: u(16), paddingLeft: u(24), paddingRight: u(24),
            }}
          >
            {DISCLOSURE.cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onSave({ ...selected, necessary: true })}
            className="reel-touch-target relative flex-1 uppercase bg-reel-cta-darkBg text-reel-chrome-textPrimary font-reel-body flex items-center justify-center text-center leading-tight hover:opacity-90 transition-opacity"
            style={{
              minHeight: u(geometry.footerH), borderRadius: u(geometry.rowRadius), fontSize: type(text.cta),
              paddingTop: u(16), paddingBottom: u(16), paddingLeft: u(24), paddingRight: u(24),
            }}
          >
            {DISCLOSURE.saveLabel}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default ConsentPanel;
